import type { ReactElement } from "react";
import { defineHFComponent } from "../component.js";
import { defineBrowserSetup } from "../runtime.js";

export type TextStaggerProps = {
  text?: string;
  size?: number;
  tone?: "ink" | "paper";
  accent?: "blue" | "green" | "violet";
};

function View({ text = "Build motion systems fast", size = 74, tone = "ink", accent = "blue" }: TextStaggerProps): ReactElement {
  const words = text.trim().split(/\s+/).filter(Boolean);
  const resolvedWords = words.length ? words : ["Build", "motion", "systems", "fast"];
  const inks = { ink: "#111827", paper: "#f8fafc" };
  const accents = { blue: "#38bdf8", green: "#34d399", violet: "#a78bfa" };
  return <div className="hf-transition-text-stagger" style={{ "--hf-stagger-size": `${Math.max(24, Math.min(160, size))}px`, "--hf-stagger-ink": inks[tone], "--hf-stagger-accent": accents[accent] } as React.CSSProperties}>{resolvedWords.map((word, index) => <span key={`${word}-${index}`}>{word}</span>)}</div>;
}

export const TextStagger = defineHFComponent<TextStaggerProps>({
  name: "text-stagger",
  View,
  css: (scope) => `
    ${scope} .hf-transition-text-stagger{display:flex;flex-wrap:wrap;gap:.24em;color:var(--hf-stagger-ink,#111827);font:900 74px/.95 Inter,system-ui,sans-serif;font-size:var(--hf-stagger-size,74px);letter-spacing:-.045em}
    ${scope} .hf-transition-text-stagger span{display:inline-block;background:linear-gradient(90deg,var(--hf-stagger-ink,#111827),var(--hf-stagger-ink,#111827),var(--hf-stagger-accent,#38bdf8),var(--hf-stagger-ink,#111827));background-size:240% 100%;-webkit-background-clip:text;background-clip:text;color:transparent;will-change:transform,opacity,filter,background-position}
  `,
  setup: defineBrowserSetup(
    () => ({}),
    ({ root, gsap }) => gsap.set(root.querySelectorAll(".hf-transition-text-stagger span"), { opacity: 0, y: 48, filter: "blur(14px)" }),
  ),
  timeline: ({ tl, select }) => {
    tl.fromTo(select(" .hf-transition-text-stagger span"), { opacity: 0, y: 48, filter: "blur(14px)" }, { opacity: 1, y: 0, filter: "blur(0px)", duration: 0.54, stagger: 0.09, ease: "power3.out" }, 0.35);
    tl.to(select(" .hf-transition-text-stagger span"), { backgroundPosition: "100% 0%", duration: 0.72, stagger: 0.04, ease: "power2.inOut" }, 1.05);
  },
});
